import useProductVariant from '@/api/inventory-management/products/useProductVariant';
import { Skeleton } from '@/components/ui/skeleton';
import { XCircleIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';

export function ProductVariantListItem({
  variantId,
  isSelected,
  onSelect,
}: {
  variantId: string;
  isSelected: boolean;
  onSelect: (variantId: string, productId: string) => void;
}) {
  const gt = useGT();
  const { data, isLoading, error } = useProductVariant(variantId);
  const productVariant = data?.productVariant;

  return (
    <li className="border-b last:border-b-0">
      {/* The radio role/selection lives on a real <button>, not the <li> —
          putting an interactive role on a noninteractive list item gives
          screen readers the wrong semantics. Mirrors VirtualizedVariantList. */}
      <button
        type="button"
        role="radio"
        aria-checked={isSelected}
        onClick={() => {
          if (productVariant) {
            onSelect(productVariant.id, productVariant.productId);
          }
        }}
        className="flex w-full items-center p-1 gap-1 text-left"
      >
        {/* Visual radio indicator — no Radix RadioGroup context wraps this. */}
        <span
          aria-hidden="true"
          className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${isSelected ? 'border-primary' : 'border-input'}`}
        >
          {isSelected && <span className="h-2 w-2 rounded-full bg-primary" />}
        </span>
        {isLoading ? (
          <Skeleton className="w-full" />
        ) : error ? (
          <>
            <XCircleIcon className="text-destructive" />
            <span className="text-destructive">
              {error.message ||
                gt('Error loading variant', {
                  context:
                    'fallback error message when a product variant fails to load',
                })}
            </span>
          </>
        ) : productVariant ? (
          <span>
            {(typeof productVariant.originMetadata?.NAMEINVC === 'string'
              ? productVariant.originMetadata.NAMEINVC
              : undefined) ||
              gt('(No name)', {
                context:
                  'fallback display name for a product variant with no name set',
              })}
          </span>
        ) : (
          <span>
            <T context="message shown when a product variant ID does not match any known variant">
              Variant not found
            </T>
          </span>
        )}
      </button>
    </li>
  );
}
